<script setup lang="ts">
import { IonIcon, modalController } from '@ionic/vue';
import { close, add, remove } from 'ionicons/icons';
import { onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{
  poster: string;
}>();

// Zoom state
const scale = ref(1);
const minScale = 0.5;
const maxScale = 4;
const zoomStep = 0.25;

// Pan state
const translateX = ref(0);
const translateY = ref(0);
let isPanning = false;
let panStartX = 0;
let panStartY = 0;
let startTranslateX = 0;
let startTranslateY = 0;

// Pinch state
let initialPinchDistance = 0;
let initialPinchScale = 1;

const containerRef = ref<HTMLElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);

function dismiss() {
  modalController.dismiss();
}

function onBackButton() {
  dismiss();
}

onMounted(() => {
  document.addEventListener('backbutton', onBackButton, false);
});

onBeforeUnmount(() => {
  document.removeEventListener('backbutton', onBackButton);
});

// Zoom controls
function zoomIn() {
  scale.value = Math.min(scale.value + zoomStep, maxScale);
  clampPan();
}

function zoomOut() {
  scale.value = Math.max(scale.value - zoomStep, minScale);
  if (scale.value <= 1) {
    translateX.value = 0;
    translateY.value = 0;
  } else {
    clampPan();
  }
}

function resetZoom() {
  scale.value = 1;
  translateX.value = 0;
  translateY.value = 0;
}

function clampPan() {
  if (scale.value <= 1) {
    translateX.value = 0;
    translateY.value = 0;
    return;
  }
  const container = containerRef.value;
  const image = imageRef.value;
  if (!container || !image) return;

  const cRect = container.getBoundingClientRect();
  const maxPanX = Math.max(0, (image.offsetWidth * scale.value - cRect.width) / 2);
  const maxPanY = Math.max(0, (image.offsetHeight * scale.value - cRect.height) / 2);
  translateX.value = Math.max(-maxPanX, Math.min(maxPanX, translateX.value));
  translateY.value = Math.max(-maxPanY, Math.min(maxPanY, translateY.value));
}

// Mouse wheel zoom
function onWheel(e: WheelEvent) {
  e.preventDefault();
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

// Touch handlers for pinch-to-zoom and pan
function getDistance(t1: Touch, t2: Touch) {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

function onTouchStart(e: TouchEvent) {
  if (e.touches.length === 2) {
    // Pinch start
    initialPinchDistance = getDistance(e.touches[0], e.touches[1]);
    initialPinchScale = scale.value;
    isPanning = false;
  } else if (e.touches.length === 1 && scale.value > 1) {
    // Pan start
    isPanning = true;
    panStartX = e.touches[0].clientX;
    panStartY = e.touches[0].clientY;
    startTranslateX = translateX.value;
    startTranslateY = translateY.value;
  }
}

function onTouchMove(e: TouchEvent) {
  if (e.touches.length === 2) {
    // Pinch zoom
    e.preventDefault();
    const dist = getDistance(e.touches[0], e.touches[1]);
    const newScale = initialPinchScale * (dist / initialPinchDistance);
    scale.value = Math.max(minScale, Math.min(maxScale, newScale));
    clampPan();
  } else if (e.touches.length === 1 && isPanning && scale.value > 1) {
    // Pan
    e.preventDefault();
    translateX.value = startTranslateX + (e.touches[0].clientX - panStartX);
    translateY.value = startTranslateY + (e.touches[0].clientY - panStartY);
    clampPan();
  }
}

function onTouchEnd(e: TouchEvent) {
  if (e.touches.length < 2) {
    initialPinchDistance = 0;
  }
  if (e.touches.length === 0) {
    isPanning = false;
  }
}

// Mouse drag for pan (desktop)
function onMouseDown(e: MouseEvent) {
  if (scale.value <= 1) return;
  isPanning = true;
  panStartX = e.clientX;
  panStartY = e.clientY;
  startTranslateX = translateX.value;
  startTranslateY = translateY.value;
  e.preventDefault();
}

function onMouseMove(e: MouseEvent) {
  if (!isPanning) return;
  translateX.value = startTranslateX + (e.clientX - panStartX);
  translateY.value = startTranslateY + (e.clientY - panStartY);
  clampPan();
}

function onMouseUp() {
  isPanning = false;
}

// Double-tap to toggle zoom
let lastTap = 0;
function onDoubleTap() {
  const now = Date.now();
  if (now - lastTap < 300) {
    if (scale.value > 1) {
      resetZoom();
    } else {
      scale.value = 2;
    }
  }
  lastTap = now;
}

const zoomPercent = () => Math.round(scale.value * 100);
</script>

<template>
  <div
    class="viewer-overlay"
    @wheel.prevent="onWheel"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    @mousemove="onMouseMove"
  >
    <!-- Close button - highly visible -->
    <button
      @click="dismiss"
      type="button"
      class="close-btn"
    >
      <ion-icon :icon="close" class="text-2xl" />
    </button>

    <!-- Zoom controls -->
    <div class="zoom-controls">
      <button @click="zoomOut" class="zoom-btn" :disabled="scale <= minScale">
        <ion-icon :icon="remove" />
      </button>
      <button @click="resetZoom" class="zoom-btn zoom-percent">
        {{ zoomPercent() }}%
      </button>
      <button @click="zoomIn" class="zoom-btn" :disabled="scale >= maxScale">
        <ion-icon :icon="add" />
      </button>
    </div>

    <!-- Image container -->
    <div
      ref="containerRef"
      class="image-container"
      @touchstart.passive="onTouchStart"
      @touchmove="onTouchMove"
      @touchend.passive="onTouchEnd"
      @mousedown="onMouseDown"
      @click="onDoubleTap"
    >
      <img
        ref="imageRef"
        :src="poster"
        alt="Poster"
        class="viewer-image"
        :style="{
          transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
          cursor: scale > 1 ? 'grab' : 'default',
        }"
        draggable="false"
      />
    </div>
  </div>
</template>

<style scoped>
.viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  touch-action: none;
  user-select: none;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1010;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(220, 38, 38, 0.8);
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.1);
}

.zoom-controls {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1010;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
}

.zoom-btn {
  width: 40px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: white;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.zoom-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
}

.zoom-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.zoom-percent {
  width: auto;
  min-width: 52px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 60px 16px 80px;
}

.viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.15s ease;
  transform-origin: center center;
  pointer-events: none;
}
</style>
